/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Grid, IconButton, Typography2, breakpoints } from '@coursera/cds-core';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { MODULE_LAYOUTS } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import { TESTIMONIAL_DISPLAY_STYLES } from 'bundles/cds-labs/components/Modules/TestimonialsModule/constants';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import type { VideoItem } from 'bundles/cds-labs/components/Modules/components/Video';
import useIsMobile from 'bundles/cds-labs/components/Modules/hooks/useIsMobile';
import useIsTablet from 'bundles/cds-labs/components/Modules/hooks/useIsTablet';
import { isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';
import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings } from 'bundles/design-system/components/types';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/cds-labs';

type GridMd = React.ComponentProps<typeof Grid>['md'];

export const IMAGE_HEIGHT = 375;

export type Testimonial = {
  id?: string | null;
  authorName?: string | null;
  authorTitle?: string | null;
  quote?: string | null;
  image?: ImageType | null;
  video?: VideoItem | null;
  associatedProduct?: string | null;
  authorOrganization?: string | null;
  productName?: string | null;
  learnerSince?: number | null;
};

export type Props = {
  testimonialItems: React.ReactNode[];
  numTestimonialsToDisplay?: 1 | 2 | 3 | 4;
  headline?: string | null;
  displayStyle?: string | null;
} & ModuleLayoutConfig;

const styles = {
  carousel: (isCarouselMode: boolean) => css`
    padding: var(--cds-spacing-400) !important;

    // This chain of selectors is to make sure the testimonials inside
    // the carousel component have the same height.
    &,
    & > div,
    .rc-Carousel,
    .slick-slider,
    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: 100%;
    }

    .slick-prev {
      left: calc(var(--cds-spacing-200) * -1);
    }

    .slick-next {
      right: calc(var(--cds-spacing-200) * -1);
    }

    [dir='rtl'] {
      .slick-prev {
        right: calc(var(--cds-spacing-200) * -1);
      }

      .slick-next {
        left: calc(var(--cds-spacing-200) * -1);
      }
    }

    .slick-arrow {
      position: absolute;
      top: 40%;
      height: auto;
      z-index: 1;

      ${breakpoints.down('xs')} {
        top: ${isCarouselMode ? `calc(${IMAGE_HEIGHT}px + var(--cds-spacing-300))` : '40%'} !important;
      }
    }

    .slick-dots {
      gap: var(--cds-spacing-100);
      margin-top: var(--cds-spacing-200);

      li {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      button {
        height: 8px;
        width: 8px;
        background-color: var(--cds-color-neutral-stroke-primary);
        border-radius: var(--cds-border-radius-100);
        transition: width 0.2s ease, background-color 0.2s ease, margin 0.2s ease;

        ::before {
          content: '' !important;
          width: 32px;
          height: 32px;
        }
      }

      .slick-active {
        button {
          width: 20px;
          height: 8px;
          background-color: var(--cds-color-neutral-primary-weak);
          border-radius: 4px;
          margin-left: -6px;
          margin-right: -6px;
        }
      }
    }
  `,
  headline: css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('md')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  iconButtonWrapper: css`
    display: inline-flex;
  `,
  iconButton: css`
    border-radius: 100%;
    display: inline-flex;
    vertical-align: middle;
  `,
  carouselWrapper: css`
    padding: 0 !important;
  `,
  testimonial: css`
    height: 100%;
  `,
};

const DEFAULT_NUM_PER_SLIDE = 4;

const TestimonialsModule = (props: Props): JSX.Element => {
  const {
    testimonialItems,
    headline,
    displayStyle,
    numTestimonialsToDisplay = DEFAULT_NUM_PER_SLIDE,
    backgroundColor,
  } = props;
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const isCarouselMode = !!displayStyle && displayStyle === TESTIMONIAL_DISPLAY_STYLES.CAROUSEL;
  const slidesToShow = isCarouselMode ? 1 : 2;
  const testimonialSingularOrPlural = slidesToShow === 1 ? _t('testimonial') : _t('testimonials');
  const isInvertedBackgroundColor = backgroundColor ? isInvertedColor(backgroundColor) : false;
  const isRTL = isRightToLeft(_t.getLocale());
  const prevArrowLabel = _t('Go to previous #{testimonialSingularOrPlural}', {
    testimonialSingularOrPlural,
  });
  const nextArrowLabel = _t('Go to next #{testimonialSingularOrPlural}', {
    testimonialSingularOrPlural,
  });

  const carouselSettings: CarouselSettings = useMemo(
    () => ({
      slidesToShow,
      slidesToScroll: slidesToShow,
      responsive: [
        // Currently, screen readers have trouble when slides have multiple testimonials.
        // This temporary solution ensures only one testimonial is visible at a time for tablet/mobile.
        // Ticket: https://coursera.atlassian.net/browse/MERCH-1441
        {
          breakpoint: breakpoints.values.md, // after fix, use breakpoints.values.sm
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            variableWidth: false,
            dots: true,
          },
        },
        // {
        //   breakpoint: breakpoints.values.md,
        //   settings: {
        //     slidesToShow,
        //     slidesToScroll: slidesToShow,
        //     variableWidth: false,
        //     dots: true,
        //   },
        // },
      ],
      isFluid: true,
      fade: false,
      dots: true,
    }),
    [slidesToShow]
  );

  const renderTestimonialContent = useCallback<() => React.ReactNode>(() => {
    // Single testimonial: render directly without carousel
    if (testimonialItems.length === 1) {
      return (
        <Grid item xs={12}>
          {React.cloneElement(testimonialItems[0] as React.ReactElement, {
            isInvertedTextColor: isInvertedBackgroundColor,
          })}
        </Grid>
      );
    }

    // Multiple testimonials on desktop (no carousel mode): render in grid
    if (!isTablet && !isMobile && slidesToShow > 1) {
      return testimonialItems?.slice(0, numTestimonialsToDisplay).map((testimonial, index) => (
        // the passed grid value will always be an integer 4 or lower, and thus always a valid grid size
        // Indexing lint is being supressed, the entire data set is being used as a callback dep array
        // eslint-disable-next-line react/no-array-index-key
        <Grid item md={(12 / numTestimonialsToDisplay) as GridMd} key={index}>
          {React.cloneElement(testimonial as React.ReactElement, {
            isInvertedTextColor: isInvertedBackgroundColor,
          })}
        </Grid>
      ));
    }

    // Multiple testimonials on tablet/mobile or carousel mode: render in carousel
    return (
      <Grid item xs={12} css={styles.carouselWrapper}>
        <Grid item xs={12} css={styles.carousel(isCarouselMode)}>
          <Carousel
            settings={{
              ...carouselSettings,
              prevArrow: (
                <div aria-label={isRTL ? nextArrowLabel : prevArrowLabel} css={styles.iconButtonWrapper}>
                  <IconButton intent={isRTL ? 'next' : 'previous'} variant="secondary" css={styles.iconButton} />
                </div>
              ),
              nextArrow: (
                <div aria-label={isRTL ? prevArrowLabel : nextArrowLabel} css={styles.iconButtonWrapper}>
                  <IconButton intent={isRTL ? 'previous' : 'next'} variant="secondary" css={styles.iconButton} />
                </div>
              ),
            }}
          >
            {testimonialItems?.slice(0, numTestimonialsToDisplay).map((testimonial, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} css={styles.testimonial}>
                {React.cloneElement(testimonial as React.ReactElement, {
                  isInvertedTextColor: isInvertedBackgroundColor,
                })}
              </div>
            ))}
          </Carousel>
        </Grid>
      </Grid>
    );
  }, [
    testimonialItems,
    isInvertedBackgroundColor,
    isTablet,
    isMobile,
    slidesToShow,
    numTestimonialsToDisplay,
    isCarouselMode,
    carouselSettings,
    isRTL,
    nextArrowLabel,
    prevArrowLabel,
  ]);

  const contentPanel = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    () => (
      <TrackedDiv
        trackingName="user_testimonial"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        {headline && (
          <Typography2 variant="titleSmall" component="h2" css={styles.headline} color="inherit">
            {headline}
          </Typography2>
        )}
        <Grid container spacing={32} aria-label={testimonialItems?.length > 1 ? _t('Testimonials') : _t('Testimonial')}>
          {renderTestimonialContent()}
        </Grid>
      </TrackedDiv>
    ),
    [headline, renderTestimonialContent, testimonialItems?.length]
  );

  return (
    <ModuleLayout assembly={{ ...props, layout: MODULE_LAYOUTS.textTopImageBottom }} renderContent={contentPanel} />
  );
};

export default TestimonialsModule;
